import React from 'react';
import { Box, Paper, Snackbar, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';

import { formatCurrency } from '../../services/productSelection';

interface MobileCartFeedbackPillProps {
  feedbackId: number;
  open: boolean;
  itemCount: number;
  total: number;
  onClose: () => void;
  onOpenCart: () => void;
}

const getProductWord = (count: number) => {
  const mod10 = count % 10;
  const mod100 = count % 100;

  if (mod10 === 1 && mod100 !== 11) return 'товар';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    return 'товара';
  }
  return 'товаров';
};

const MobileCartFeedbackPill: React.FC<MobileCartFeedbackPillProps> = ({
  feedbackId,
  open,
  itemCount,
  total,
  onClose,
  onOpenCart,
}) => {
  const theme = useTheme();

  return (
    <Snackbar
      key={feedbackId}
      open={open}
      autoHideDuration={2800}
      onClose={(_, reason) => {
        if (reason !== 'clickaway') onClose();
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{
        bottom: 'max(18px, env(safe-area-inset-bottom)) !important',
      }}
    >
      <Paper
        component="button"
        type="button"
        elevation={12}
        aria-label={`Добавлено в корзину. ${itemCount} ${getProductWord(itemCount)}, ${formatCurrency(total)}. Перейти в корзину`}
        onClick={onOpenCart}
        sx={{
          width: 'min(280px, calc(100vw - 32px))',
          minHeight: 52,
          px: 1.25,
          py: 0.75,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.24)}`,
          borderRadius: '26px',
          display: 'flex',
          alignItems: 'center',
          gap: 1.1,
          color: theme.palette.text.primary,
          backgroundColor: alpha(theme.palette.background.paper, 0.96),
          backdropFilter: 'blur(14px)',
          boxShadow: '0 14px 34px rgba(15, 23, 42, 0.28)',
          textAlign: 'left',
          cursor: 'pointer',
          appearance: 'none',
          '&:hover': {
            backgroundColor: alpha(theme.palette.background.paper, 1),
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
            color: '#ffffff',
            backgroundColor: theme.palette.success.main,
          }}
        >
          <CheckRoundedIcon sx={{ fontSize: 19 }} />
        </Box>
        <Box
          aria-live="polite"
          aria-atomic="true"
          sx={{ flexGrow: 1, minWidth: 0 }}
        >
          <Typography
            component="span"
            sx={{
              display: 'block',
              fontSize: '0.76rem',
              fontWeight: 800,
              lineHeight: 1.2,
            }}
          >
            Добавлено в корзину
          </Typography>
          <Typography
            component="span"
            sx={{
              display: 'block',
              mt: 0.2,
              color: 'text.secondary',
              fontSize: '0.68rem',
              lineHeight: 1.2,
            }}
          >
            {itemCount} {getProductWord(itemCount)} · {formatCurrency(total)}
          </Typography>
        </Box>

        <ChevronRightRoundedIcon
          sx={{ flexShrink: 0, color: 'primary.main', fontSize: 21 }}
        />
      </Paper>
    </Snackbar>
  );
};

export default MobileCartFeedbackPill;
